import React, { useEffect } from 'react';
import Swal from 'sweetalert2';
import Quill from 'quill';
import 'quill/dist/quill.snow.css';
import TableUp, { defaultCustomSelect, TableSelection, TableAlign, TableResizeLine, TableResizeScale, TableMenuContextmenu, TableVirtualScrollbar } from 'quill-table-up';
import 'quill-table-up/index.css';
import 'quill-table-up/table-creator.css';

Quill.register({ [`modules/${TableUp.moduleName}`]: TableUp }, true);

function RichTextEditor({ value, onChange, placeholder }) {
    const editorRef = React.useRef(null);
    const quillRef = React.useRef(null);
    const toolbarRef = React.useRef(null);

    useEffect(() => {
        if (!editorRef.current) return;
        const toolbar = [
            [{ 'header': [1, 2, 3, 4, 5, false] }],
            ['bold', 'italic', 'underline', 'strike', 'link'],
            [{ 'color': [] }, { 'background': [] }],
            [{ 'list': 'ordered' }, { 'list': 'bullet' }],
            [{ 'size': ['small', false, 'large', 'huge'] }],
            ['code-block', 'image', { [TableUp.toolName]: [] }],
            ['clean']
        ];
        const quill = new Quill(editorRef.current, {
            theme: 'snow',
            placeholder: placeholder || '',
            modules: {
                toolbar: { container: toolbar },
                [TableUp.moduleName]: {
                    toolbarTable: true,
                    customSelect: defaultCustomSelect,
                    modules: [
                        { module: TableSelection },
                        { module: TableAlign },
                        { module: TableResizeLine },
                        { module: TableResizeScale, options: { blockSize: 12 } },
                        { module: TableMenuContextmenu, options: { tipText: true } },
                        { module: TableVirtualScrollbar }
                    ]
                }
            }
        });
        quillRef.current = quill;
        // Keep the (hidden by default) link/formula tooltip out of the a11y tree
        const tooltipRoot = quill.theme && quill.theme.tooltip ? quill.theme.tooltip.root : null;
        let tooltipObserver = null;
        if (tooltipRoot) {
            const syncTooltipAria = () => {
                tooltipRoot.setAttribute('aria-hidden', tooltipRoot.classList.contains('ql-hidden') ? 'true' : 'false');
            };
            syncTooltipAria();
            tooltipObserver = new MutationObserver(syncTooltipAria);
            tooltipObserver.observe(tooltipRoot, { attributes: true, attributeFilter: ['class'] });
        }
        // Ensure editor area is visibly clickable and focusable
        quill.root.style.minHeight = '200px';
        const containerEl = quill.container;
        if (containerEl) {
            containerEl.setAttribute('tabindex', '0');
        }
        // Make sure clipboard stays contenteditable (Quill usually sets it, but we enforce it just in case)
        const clipboardEl = editorRef.current.querySelector('.ql-clipboard');
        if (clipboardEl && clipboardEl.getAttribute('contenteditable') !== 'true') {
            clipboardEl.setAttribute('contenteditable', 'true');
        }
        const tableModule = quill.getModule(TableUp.moduleName);
        if (tableModule && tableModule.insertTable) {
            const originalInsert = tableModule.insertTable.bind(tableModule);
            tableModule.insertTable = (rows, cols, source) => {
                const range = quill.getSelection(true);
                let insideTable = false;
                if (range) {
                    const leaf = quill.getLeaf(range.index)[0];
                    let node = leaf && leaf.domNode;
                    while (node && node !== quill.root) {
                        if (node.matches && (node.matches('.ql-table') || node.matches('table'))) {
                            insideTable = true;
                            break;
                        }
                        node = node.parentNode;
                    }
                }
                if (insideTable) {
                    Swal.fire({
                        text: 'You can’t insert a table inside another table.',
                        icon: 'warning',
                        background: '#1a1a1a',
                        color: '#b3b3b3',
                        iconColor: '#ffcc00',
                        confirmButtonText: 'OK',
                        customClass: { popup: 'swal2-dark-popup', confirmButton: 'swal2-dark-button' }
                    });
                    return;
                }
                try {
                    originalInsert(rows, cols, source);
                } catch (e) {
                    Swal.fire({
                        text: 'Inserting a table is not available at the current location.',
                        icon: 'error',
                        background: '#1a1a1a',
                        color: '#b3b3b3',
                        iconColor: '#ff0000',
                        confirmButtonText: 'OK',
                        customClass: { popup: 'swal2-dark-popup', confirmButton: 'swal2-dark-button' }
                    });
                }
            };
        }
        const maybeToolbar = editorRef.current && editorRef.current.previousElementSibling;
        if (maybeToolbar && maybeToolbar.classList && maybeToolbar.classList.contains('ql-toolbar')) {
            toolbarRef.current = maybeToolbar;
        }
        if (value) {
            quill.clipboard.dangerouslyPasteHTML(value);
        }
        const handleChange = () => onChange && onChange(quill.root.innerHTML);
        quill.on('text-change', handleChange);

        // Support inserting public image URLs: toolbar prompt + paste URL => embed
        const isProbablyImageUrl = (url) => {
            if (!url || typeof url !== 'string') return false;
            if (!/^https?:\/\//i.test(url)) return false;
            return /(\.(png|jpe?g|gif|webp|bmp|svg)(\?.*)?$)/i.test(url);
        };
        const insertImageAtSelection = (url) => {
            const range = quill.getSelection(true);
            const index = range ? range.index : quill.getLength();
            quill.insertEmbed(index, 'image', url, 'user');
            quill.setSelection(index + 1, 0, 'silent');
        };
        const toolbarModule = quill.getModule('toolbar');
        if (toolbarModule) {
            toolbarModule.addHandler('image', async () => {
                const result = await Swal.fire({
                    title: 'Insert image URL',
                    input: 'url',
                    inputLabel: 'Image URL',
                    inputPlaceholder: 'https://example.com/image.jpg',
                    inputAttributes: { autocapitalize: 'off', spellcheck: 'false' },
                    showCancelButton: true,
                    confirmButtonText: 'Insert',
                    background: '#1a1a1a',
                    color: '#b3b3b3',
                    iconColor: '#ffcc00',
                    customClass: { popup: 'swal2-dark-popup', confirmButton: 'swal2-dark-button' }
                });
                const url = result && result.value ? String(result.value).trim() : '';
                if (!url) return;
                insertImageAtSelection(url);
            });
        }
        const handlePaste = (e) => {
            if (!e.clipboardData) return;
            const text = e.clipboardData.getData('text');
            if (text && isProbablyImageUrl(text.trim())) {
                e.preventDefault();
                insertImageAtSelection(text.trim());
            }
        };
        quill.root.addEventListener('paste', handlePaste);

        const handleContainerMouseDown = (e) => {
            const toolbarEl = toolbarRef.current;
            if (toolbarEl && toolbarEl.contains(e.target)) return;
            const editorEl = quill.root;
            if (!editorEl.contains(e.target)) {
                quill.focus();
                const len = quill.getLength();
                quill.setSelection(Math.max(0, len - 1), 0);
                return;
            }

            setTimeout(() => {
                const sel = quill.getSelection();
                if (!sel) {
                    quill.focus();
                    const len = quill.getLength();
                    quill.setSelection(Math.max(0, len - 1), 0);
                }
            }, 0);
        };
        if (editorRef.current) {
            // Capture phase to run before any plugin handlers
            editorRef.current.addEventListener('mousedown', handleContainerMouseDown, true);
        }
        if (containerEl) {
            containerEl.addEventListener('mousedown', handleContainerMouseDown, true);
        }
        // Also attach to potential overlay from table virtual scrollbar
        const overlays = editorRef.current.querySelectorAll('.table-up-scrollbar--virtual');
        overlays.forEach((el) => {
            el.addEventListener('mousedown', handleContainerMouseDown, true);
        });

        // Keyboard patterns
        quill.keyboard.addBinding({ key: ' ', collapsed: true }, (range) => {
            const res = quill.getLine(range.index);
            if (!res) return true;
            const line = res[0];
            const offset = res[1];
            const lineStart = quill.getIndex(line);
            const prefix = quill.getText(lineStart, offset);
            if (/^(\-|\*)\s$/.test(prefix)) {
                quill.deleteText(lineStart, prefix.length);
                quill.formatLine(lineStart, 1, { list: 'bullet' });
                return false;
            }
            if (/^\d+[\.)]\s$/.test(prefix)) {
                quill.deleteText(lineStart, prefix.length);
                quill.formatLine(lineStart, 1, { list: 'ordered' });
                return false;
            }
            return true;
        });

        return () => {
            quill.off('text-change', handleChange);
            if (quill && quill.root) {
                quill.root.removeEventListener('paste', handlePaste);
            }
            if (editorRef.current) {
                editorRef.current.removeEventListener('mousedown', handleContainerMouseDown, true);
            }
            const containerCleanupEl = quill.container;
            if (containerCleanupEl) {
                containerCleanupEl.removeEventListener('mousedown', handleContainerMouseDown, true);
            }
            // No strict need to remove from overlays as elements may be gone, but try anyway
            const overlaysCleanup = editorRef.current ? editorRef.current.querySelectorAll('.table-up-scrollbar--virtual') : [];
            overlaysCleanup.forEach((el) => {
                el.removeEventListener('mousedown', handleContainerMouseDown, true);
            });
            if (toolbarRef.current && toolbarRef.current.parentNode) {
                toolbarRef.current.parentNode.removeChild(toolbarRef.current);
                toolbarRef.current = null;
            }
            if (editorRef.current) {
                editorRef.current.innerHTML = '';
            }
            if (tooltipObserver) {
                tooltipObserver.disconnect();
            }
            quillRef.current = null;
        };
    }, []);

    useEffect(() => {
        const quill = quillRef.current;
        if (!quill) return;
        const current = quill.root.innerHTML;
        if (value !== undefined && value !== current) {
            const selection = quill.getSelection();
            quill.clipboard.dangerouslyPasteHTML(value || '');
            if (selection) quill.setSelection(selection);
        }
    }, [value]);

    return (
        <div
            ref={editorRef}
            style={{minHeight: '200px' }}
        />
    );
}

export default RichTextEditor;


